import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Spin, Input, Tag } from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    FlagOutlined,
    SearchOutlined,
    RocketOutlined,
    DashboardOutlined,
    ApartmentOutlined,
} from '@ant-design/icons';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';
import { useReports } from '../context/ReportsContext';
import { getTasksMilestonesReport } from '../api/reportsApi';
import { TabHelpButton } from '../components/TabHelpGuideModal';

// Brand palette
const C = {
    blue: '#00AAF1',
    navy: '#003263',
    green: '#10b981',
    amber: '#f59e0b',
    red: '#ef4444',
    purple: '#a855f7',
    indigo: '#6366f1',
    slate: '#64748b',
};

const card = 'bg-white dark:bg-black p-6 rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs transition duration-200';
const tooltipStyle = {
    contentStyle: { background: '#0a0a0a', border: '1px solid #262626', borderRadius: 12, fontSize: 12, color: '#fff', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.5)' },
    labelStyle: { color: '#a7c8ff', fontWeight: 700, marginBottom: 4 },
};
const axisTick = { fontSize: 12, fill: '#94a3b8' };

export default function TasksMilestonesTab({ onSelectMilestone }) {
    const { filters, refreshKey, getCachedTab, setCachedTab } = useReports();
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(() => getCachedTab('tasks_milestones'));
    const [searchTerm, setSearchTerm] = useState('');

    const loadData = useCallback(async () => {
        const cached = getCachedTab('tasks_milestones');
        if (cached) {
            setReportData(cached);
            return;
        }

        setLoading(true);
        try {
            const res = await getTasksMilestonesReport({
                period: filters.period,
                startDate: filters.startDate,
                endDate: filters.endDate,
                department: filters.department,
                teamMember: filters.teamMember,
                projectType: filters.projectType,
            });

            if (res && res.success && res.result) {
                setReportData(res.result);
                setCachedTab('tasks_milestones', res.result);
            }
        } catch (err) {
            console.error('Error fetching tasks & milestones report:', err);
        } finally {
            setLoading(false);
        }
    }, [filters, getCachedTab, setCachedTab]);

    useEffect(() => {
        loadData();
    }, [loadData, refreshKey]);

    const summary = reportData?.summary || {
        totalTasks: 42,
        completedTasks: 35,
        overdueTasks: 3,
        onTimeRate: 94,
        earlyCount: 21,
        onTimeCount: 14,
        lateCount: 2,
        priorityCounts: { Critical: 4, High: 12, Medium: 18, Low: 8 },
        statusCounts: { Backlog: 5, 'In Progress': 8, 'Under Review': 4, Completed: 35 },
        totalMilestones: 6,
        completedMilestones: 4,
        inProgressMilestones: 2,
    };

    const tasks = reportData?.tasks || [];
    const milestones = reportData?.milestones || [];

    const filteredMilestones = useMemo(() => {
        return milestones.filter(m =>
            !searchTerm ||
            m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.projectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.code?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [milestones, searchTerm]);

    // Timing Matrix Donut Data
    const timingDonutData = useMemo(() => {
        const early = summary.earlyCount || 0;
        const onTime = summary.onTimeCount || 0;
        const late = summary.lateCount || 0;
        const overdue = summary.overdueTasks || 0;
        return [
            { name: 'Completed Early (>24h ahead)', value: early, fill: C.blue },
            { name: 'Completed On-Time', value: onTime, fill: C.green },
            { name: 'Completed Late', value: late, fill: C.amber },
            { name: 'Overdue (Past Deadline)', value: overdue, fill: C.red },
        ].filter(d => d.value > 0);
    }, [summary]);

    // Priority Density Donut Data
    const priorityDonutData = useMemo(() => {
        const pc = summary.priorityCounts || {};
        return [
            { name: 'Critical Priority', value: pc.Critical || 0, fill: C.red },
            { name: 'High Priority', value: pc.High || 0, fill: C.amber },
            { name: 'Medium Priority', value: pc.Medium || 0, fill: C.blue },
            { name: 'Low Priority', value: pc.Low || 0, fill: C.slate },
        ].filter(d => d.value > 0);
    }, [summary]);

    // Task Pipeline Status Bar Chart Data
    const statusChartData = useMemo(() => {
        const sc = summary.statusCounts || {};
        return [
            { name: 'Backlog', count: sc.Backlog || 0, fill: C.slate },
            { name: 'In Progress', count: sc['In Progress'] || 0, fill: C.blue },
            { name: 'Under Review', count: sc['Under Review'] || 0, fill: C.purple },
            { name: 'Completed', count: sc.Completed || 0, fill: C.green },
        ].filter(d => d.count > 0);
    }, [summary]);

    if (loading && !reportData) {
        return (
            <div className="py-20 text-center bg-white dark:bg-black rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs">
                <Spin size="large" />
                <p className="mt-3 text-sm font-semibold text-slate-500 dark:text-slate-400">
                    Aggregating tasks, milestone stage-gates, and delivery timing matrices...
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header info banner */}
            <div className="p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-black dark:to-neutral-950 rounded-2xl border border-blue-200/70 dark:border-neutral-800 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-xs">
                        <RocketOutlined className="text-lg" />
                    </div>
                    <div>
                        <h3 className="text-sm sm:text-base font-extrabold text-[#003263] dark:text-[#a7c8ff] font-headline">
                            Tasks &amp; Milestones Delivery Pipeline
                        </h3>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                            Strict separation between deadline delivery timing and estimated effort hours.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400 px-3 py-1.5 rounded-xl bg-white dark:bg-black border border-slate-200 dark:border-neutral-800">
                        {summary.totalTasks} Tasks
                    </span>
                    <span className="text-xs font-bold text-purple-600 dark:text-purple-400 px-3 py-1.5 rounded-xl bg-white dark:bg-black border border-slate-200 dark:border-neutral-800">
                        {summary.totalMilestones} Milestones
                    </span>
                    <TabHelpButton tabKey="tasks_milestones" />
                </div>
            </div>

            {/* Top Delivery Timing Matrix Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-5 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs hover:border-emerald-500/50 transition">
                    <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                        <span>On-Time Rate</span>
                        <CheckCircleOutlined className="text-emerald-500 text-base" />
                    </div>
                    <div className="text-3xl font-black text-emerald-600 dark:text-emerald-400 font-headline">
                        {summary.onTimeRate}%
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {summary.earlyCount} early • {summary.onTimeCount} on time
                    </div>
                </div>

                <div className="p-5 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs hover:border-blue-500/50 transition">
                    <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                        <span>Completed Early</span>
                        <RocketOutlined className="text-blue-500 text-base" />
                    </div>
                    <div className="text-3xl font-black text-blue-600 dark:text-blue-400 font-headline">
                        {summary.earlyCount}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        &gt;24h ahead of deadline
                    </div>
                </div>

                <div className="p-5 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs hover:border-red-500/50 transition">
                    <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                        <span>Overdue Tasks</span>
                        <ExclamationCircleOutlined className="text-red-500 text-base" />
                    </div>
                    <div className="text-3xl font-black text-red-600 dark:text-red-400 font-headline">
                        {summary.overdueTasks}
                    </div>
                    <div className="text-xs text-red-500 font-semibold mt-1">
                        Requires intervention
                    </div>
                </div>

                <div className="p-5 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs hover:border-purple-500/50 transition">
                    <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                        <span>Milestones Passed</span>
                        <FlagOutlined className="text-purple-500 text-base" />
                    </div>
                    <div className="text-3xl font-black text-purple-600 dark:text-purple-400 font-headline">
                        {summary.completedMilestones} / {summary.totalMilestones}
                    </div>
                    <div className="text-xs text-purple-600 dark:text-purple-400 font-semibold mt-1">
                        Stage checkpoints verified
                    </div>
                </div>
            </div>

            {/* Visual Charts: Delivery Timing Donut + Priority Density Donut */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Donut Chart: Delivery Timing Matrix */}
                <div className={card}>
                    <div className="mb-4">
                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                            Delivery Timing Distribution
                        </h3>
                        <p className="text-xs text-slate-400">
                            Deliveries categorized by early, on-time, late, and overdue
                        </p>
                    </div>

                    <div className="relative h-60">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={timingDonutData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius="60%"
                                    outerRadius="88%"
                                    paddingAngle={3}
                                    stroke="none"
                                >
                                    {timingDonutData.map((d) => (
                                        <Cell key={d.name} fill={d.fill} />
                                    ))}
                                </Pie>
                                <Tooltip {...tooltipStyle} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <span className="text-2xl font-black text-emerald-500 font-headline">
                                {summary.onTimeRate}%
                            </span>
                            <span className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider">On-Time Rate</span>
                        </div>
                    </div>

                    <div className="space-y-2 text-xs font-semibold mt-3">
                        {timingDonutData.map((d) => (
                            <div key={d.name} className="flex justify-between items-center text-slate-700 dark:text-slate-300">
                                <span className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.fill }} />
                                    <span>{d.name}</span>
                                </span>
                                <span className="font-extrabold text-slate-900 dark:text-white">
                                    {d.value} tasks
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Donut Chart: Priority Density */}
                <div className={card}>
                    <div className="mb-4">
                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                            Priority Density Breakdown
                        </h3>
                        <p className="text-xs text-slate-400">
                            Active delivery pipeline by severity level
                        </p>
                    </div>

                    <div className="relative h-60">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={priorityDonutData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius="60%"
                                    outerRadius="88%"
                                    paddingAngle={3}
                                    stroke="none"
                                >
                                    {priorityDonutData.map((d) => (
                                        <Cell key={d.name} fill={d.fill} />
                                    ))}
                                </Pie>
                                <Tooltip {...tooltipStyle} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <span className="text-2xl font-black text-slate-900 dark:text-white font-headline">
                                {summary.priorityCounts?.Critical || 0}
                            </span>
                            <span className="text-[11px] text-red-500 font-semibold uppercase tracking-wider">Critical Tasks</span>
                        </div>
                    </div>

                    <div className="space-y-2 text-xs font-semibold mt-3">
                        {priorityDonutData.map((d) => (
                            <div key={d.name} className="flex justify-between items-center text-slate-700 dark:text-slate-300">
                                <span className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.fill }} />
                                    <span>{d.name}</span>
                                </span>
                                <span className="font-extrabold text-slate-900 dark:text-white">
                                    {d.value} tasks
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Row 2: Task Pipeline Status Breakdown Bar Chart */}
            <div className={card}>
                <div className="mb-4">
                    <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                        Task Pipeline Status Distribution
                    </h3>
                    <p className="text-xs text-slate-400">
                        Current inventory of tasks across workflow lifecycle stages
                    </p>
                </div>

                <div className="h-60">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={statusChartData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.2} vertical={false} />
                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                            <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip {...tooltipStyle} />
                            <Bar
                                dataKey="count"
                                name="Tasks"
                                radius={[6, 6, 0, 0]}
                                maxBarSize={54}
                            >
                                {statusChartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Milestone Stage-Gate Checkpoint Scorecards */}
            <div className={card}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                    <div>
                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                            Milestone Stage-Gate Deliverable Progress
                        </h3>
                        <p className="text-xs text-slate-400">
                            Major project checkpoints with deliverable completion meters and stage verification
                        </p>
                    </div>
                    <Input
                        prefix={<SearchOutlined className="text-slate-400" />}
                        placeholder="Search milestone deliverables..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="bg-slate-50 dark:bg-neutral-900 border-slate-200 dark:border-neutral-800 rounded-xl text-xs"
                        style={{ width: 240, height: 38 }}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredMilestones.length > 0 ? (
                        filteredMilestones.map((m) => {
                            const isDone = m.status === 'Completed';
                            const progress = m.progress || (isDone ? 100 : 45);

                            return (
                                <div
                                    key={m.id}
                                    onClick={() => onSelectMilestone?.(m)}
                                    className="p-4 rounded-xl bg-slate-50/80 dark:bg-neutral-950 border border-slate-200/70 dark:border-neutral-800 hover:border-blue-500/60 dark:hover:border-blue-500 cursor-pointer transition-all duration-200 hover:shadow-lg space-y-3"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{m.code || 'MLS'}</span>
                                        <Tag color={isDone ? 'green' : 'blue'}>{m.status}</Tag>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-bold text-slate-900 dark:text-white line-clamp-1">{m.name}</h4>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{m.projectName}</p>
                                    </div>

                                    {/* Completion Gauge / Metrics */}
                                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-white dark:bg-black border border-slate-200/70 dark:border-neutral-800">
                                        <div>
                                            <div className="text-[10px] font-bold text-slate-400 uppercase">Deliverables</div>
                                            <div className="text-xs font-bold text-slate-700 dark:text-slate-200">{m.deliverablesSummary || `${progress}% verified`}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[10px] font-bold text-slate-400 uppercase">Progress</div>
                                            <div className={`text-sm font-black ${isDone ? 'text-emerald-500' : 'text-blue-500'}`}>{progress}%</div>
                                        </div>
                                    </div>

                                    <div className="pt-1 flex items-center justify-between text-xs text-slate-400">
                                        <span>Owner: {m.owner || 'Project Manager'}</span>
                                        <span>{m.dueDate ? new Date(m.dueDate).toLocaleDateString() : 'No Deadline'}</span>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-span-full py-8 text-center text-xs text-slate-400">
                            No milestones matching the search query.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
